import { type AllSseEvents, type SseStreamEvent } from '@sparrow-card/sparrow-card-constants';
import { createWaitForSseEvent } from 'utils/sse/createWaitForSseEvent';
import { SseError } from 'utils/sse/SseError';
import { buildApplicationSseUrl } from 'utils/sse/waitForSseEvent';

type ApplicationSseSession = {
  submissionId: string;
  eventSource: EventSource;
  abortController: AbortController;
};

let activeSession: ApplicationSseSession | null = null;

export const closeApplicationSseSession = (): void => {
  if (!activeSession) {
    return;
  }

  const session = activeSession;

  activeSession = null;
  session.abortController.abort();
  session.eventSource.close();
};

export const openApplicationSseSession = (submissionId: string): ApplicationSseSession => {
  if (activeSession?.submissionId === submissionId) {
    return activeSession;
  }

  closeApplicationSseSession();

  activeSession = {
    submissionId,
    eventSource: new EventSource(buildApplicationSseUrl(submissionId)),
    abortController: new AbortController(),
  };

  return activeSession;
};

export const getApplicationSseSession = (): ApplicationSseSession | null => activeSession;

export const ensureApplicationSseSession = (submissionId: string): ApplicationSseSession => {
  if (activeSession?.submissionId === submissionId) {
    return activeSession;
  }

  return openApplicationSseSession(submissionId);
};

export const waitForApplicationSseEvent = async ({
  resolveEvents,
  closeOnEvents,
}: {
  resolveEvents: AllSseEvents[];
  closeOnEvents: AllSseEvents[];
}): Promise<SseStreamEvent> => {
  if (!activeSession) {
    throw new SseError('no active application session');
  }

  const { submissionId, eventSource, abortController } = activeSession;

  const event = await createWaitForSseEvent(buildApplicationSseUrl(submissionId), resolveEvents, {
    eventSource,
    closeOnEvents,
    signal: abortController.signal,
  });

  if (closeOnEvents.includes(event.event as AllSseEvents)) {
    if (activeSession?.eventSource === eventSource) {
      closeApplicationSseSession();
    } else {
      eventSource.close();
    }
  }

  return event;
};
